import React, { useEffect, useMemo } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  chakra,
  Box,
  Select,
  useColorMode,
  IconButton,
  Input,
  Tfoot,
  Flex,
  Center,
  HStack,
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  VStack,
  Checkbox,
} from "@chakra-ui/react";
import { GoTriangleUp, GoTriangleDown } from "react-icons/go";
import { useTable, useSortBy, usePagination } from "react-table";
import { AiOutlineLeft, AiOutlineRight } from "react-icons/ai";
import { useIntl } from "react-intl";
import messages from "./messages";
import getMoneyFormat from "../../utils/getMoneyFormat";
import { IoWarningOutline } from "react-icons/io5";
import { MdVisibility} from 'react-icons/md';

const rowsNo = [10, 25, 50, 100, 150, 200];


const DynamicTable = ({
  data,
  columns,
  totalCount,
  fetchData,
  loading,
  hidePagination,
  skipPageReset,
  updateMyData,
  totals,
  controlledPageCount,
  smallWidth,
  summary,
  params,
  customPageIndex,
  getPageIndex,
  getPageSize,
  customPageSize,
  updateIsAcknowledged,
  dataNotFoundText,
  featureMarkDeliveredAllowedParentSupplierIds,
  onShowSiblings,
}: any) => {
  const { colorMode } = useColorMode();

  const allHeaders = React.useMemo(
    () => columns.map((c: any) => c.Header),
    [columns]
  );

  const initialSelected = React.useMemo(() => {
    return columns
      .filter((c: any) => c?.isDefault !== false)
      .map((c: any) => c.Header);
  }, [columns]);

  const [selectedHeaders, setSelectedHeaders] = React.useState<string[]>(initialSelected);
  const [draftHeaders, setDraftHeaders] = React.useState<string[]>(initialSelected);
  const [cmIsOpen, setCmIsOpen] = React.useState(false);

  const openColumnsPopover = () => {
    setDraftHeaders(selectedHeaders);
    setCmIsOpen(true);
  };
  const closeColumnsPopover = () => setCmIsOpen(false);
  const toggleDraftHeader = (h: string) => {
    setDraftHeaders((prev) => {
      const next = prev.includes(h) ? prev.filter((x) => x !== h) : [...prev, h];
      return next.length === 0 ? prev : next;
    });
  };
  const applyDraftHeaders = () => {
    setSelectedHeaders(draftHeaders);
    setCmIsOpen(false);
  };

  useEffect(() => {
    const existingHeaderSet = new Set(columns.map((c: any) => c.Header));
    const defaultHeaders = columns
      .filter((c: any) => c?.isDefault !== false)
      .map((c: any) => c.Header);

    setSelectedHeaders((prev) => {
      const preserved = prev.filter((h) => existingHeaderSet.has(h));
      const merged = Array.from(new Set([...preserved, ...defaultHeaders]));
      return merged;
    });

    setDraftHeaders((prev) => {
      const preserved = prev.filter((h) => existingHeaderSet.has(h));
      const merged = Array.from(new Set([...preserved, ...defaultHeaders]));
      return merged;
    });
  }, [columns]);

  const managedColumns = React.useMemo(() => {
    return columns.filter((c: any) => selectedHeaders.includes(c.Header));
  }, [columns, selectedHeaders]);

  const stableParams = useMemo(() => params ?? {}, [JSON.stringify(params ?? {})]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable<any>(
    {
      columns: managedColumns,
      data,
      //@ts-ignore
      manualPagination: true,
      pageCount: controlledPageCount,
      autoResetPage: !skipPageReset,
      updateMyData,
      featureMarkDeliveredAllowedParentSupplierIds,
      updateIsAcknowledged,
      onShowSiblings,
      initialState: {
        pageIndex: customPageIndex ? customPageIndex : 0,
        pageSize: customPageSize ? customPageSize : 150,
      } as any,
    },
    useSortBy,
    usePagination
  ) as any;

  useEffect(() => {
    if (pageSize) {
      fetchData({
        offset: pageSize * pageIndex,
        limit: pageSize,
        ...stableParams,
      });
    }
  }, [pageSize, pageIndex, stableParams]);

  const tableHeadRow = headerGroups.map((headerGroup: any) => {
    const { key: key1, ...restHeaderProps } = headerGroup.getHeaderGroupProps();
    return (
      <Tr key={key1} {...restHeaderProps} height={{ base: "10vh", lg: "3rem" }}>
        {headerGroup.headers.map((column: any, index: any) => {
          const { key: key2, ...restColumnProps } = column.getHeaderProps(
            column.getSortByToggleProps()
          );
          let borderRight = index < headerGroup.headers.length - 1 ? "1px" : "0px";
          return (
            <Th
              key={key2}
              {...restColumnProps}
              isNumeric={column.isNumeric}
              className="h-full bg-blueGray-800 text-white"
              p={1}
              borderRightWidth={borderRight}
              textAlign="center"
              style={{ wordBreak: "keep-all" }}
            >
              {column.render("Header")}
              <chakra.span pl="0">
                {column.isSorted ? (
                  column.isSortedDesc ? (
                    <GoTriangleDown aria-label="sorted descending" />
                  ) : (
                    <GoTriangleUp aria-label="sorted ascending" />
                  )
                ) : null}
              </chakra.span>
            </Th>
          );
        })}
      </Tr>
    );
  });

  const tableCells = page.map((row: any) => {
    prepareRow(row);
    const { key: key3, ...restRowProps } = row.getRowProps();
    return (
      <Tr
        key={key3}
        {...restRowProps}
        borderStyle="solid"
        borderBottomWidth="1px"
        className="border-gray-400"
      >
        {row.cells.map((cell: any, index: any, cells: any) => {
          const { key: key4, ...restCellProps } = cell.getCellProps();
          let borderRight = index < row.cells.length - 1 ? "1px" : "0px";
          return (
            <Td
              py={cell?.column?.Header === "Date" ? 0 : 2}
              px={cell?.column?.Header === "Date" ? 0 : 2}
              maxWidth={`${100 / cells.length}vw`}
              key={key4}
              {...restCellProps}
              isNumeric={cell.column.isNumeric}
              borderRightWidth={borderRight}
              textAlign="center"
              wordBreak="break-all"
            >
              {cell.render("Cell")}
            </Td>
          );
        })}
      </Tr>
    );
  });

  return (
    <>
      {totalCount > 0 && (
        <HStack justifyContent="space-between" mb={2}>
        </HStack>
      )}

      <Box
        overflowX="auto"
        width="100%"
        borderBottomRightRadius="10px"
        flex={1}
        position="relative"
      >
        {totalCount > 0 && (
          <Popover isOpen={cmIsOpen} onClose={closeColumnsPopover} placement="bottom-start">
            <PopoverTrigger>
              <Box
                position="absolute"
                top="8px"
                left="2px"
                zIndex={10}
                cursor="pointer"
                onClick={openColumnsPopover}
                color={totalCount === 0 ? "white" : "white"}
                _hover={{ color: "blue.200" }}
                display="flex"
                alignItems="center"
                justifyContent="center"
                width="32px"
                height="32px"
              >
                <MdVisibility size={20} />
              </Box>
            </PopoverTrigger>
            <PopoverContent width="200px">
              <PopoverArrow />
              <PopoverBody p={3}>
                <VStack align="stretch" spacing={2}>
                  {allHeaders.map((h: string) => (
                    <Checkbox
                      key={h}
                      isChecked={draftHeaders.includes(h)}
                      onChange={() => toggleDraftHeader(h)}
                      size="sm"
                    >
                      {h}
                    </Checkbox>
                  ))}
                  <HStack spacing={2} mt={2}>
                    <Button size="xs" colorScheme="blue" onClick={applyDraftHeaders}>
                      Done
                    </Button>
                    <Button size="xs" variant="ghost" onClick={closeColumnsPopover}>
                      Cancel
                    </Button>
                  </HStack>
                </VStack>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        )}

        {data.length > 0 && !loading ? (
          <Table
            overflowX="auto"
            className="min-h-1/2"
            variant="unstyled"
            {...getTableProps()}
            minWidth={{ base: smallWidth ? smallWidth : 800 }}
            h={"1px"}
          >
            <Thead>{tableHeadRow}</Thead>
            <Tbody
              backgroundColor={colorMode === "dark" ? "black" : "white"}
              {...getTableBodyProps()}
            >
              {tableCells}
            </Tbody>
            {(totals || summary) && (
              <Tfoot>
                {totals && (
                  <Tr>
                    <Th></Th>
                    <Th></Th>
                    <Th fontSize={{ base: 16, lg: "1.3vw" }} textAlign="center">
                      Total
                    </Th>
                    <Th fontSize={{ base: 16, lg: "1.3vw" }} textAlign="center">
                      {totals.totalOrderedQuantity}
                    </Th>
                  </Tr>
                )}
                {summary && (
                  <Tr>
                    <Th></Th>
                    <Th></Th>
                    <Th fontSize={{ base: 16, lg: "1.3vw" }} textAlign="center">
                      Total
                    </Th>
                    <Th fontSize={{ base: 16, lg: "1.3vw" }} textAlign="center">
                      {summary.totalOrderedQuantity}
                    </Th>
                    <Th></Th>
                    {headerGroups[0].headers.length > 6 && <Th></Th>}
                    <Th fontSize={{ base: 16, lg: "1.3vw" }} textAlign="center">
                      {getMoneyFormat(summary.totalAmount)}
                    </Th>
                  </Tr>
                )}
              </Tfoot>
            )}
          </Table>
        ) : !loading && data.length === 0 ? (
          <Flex
            minH="40vh"
            minWidth={{ base: smallWidth ? smallWidth : 800 }}
            justifyContent="center"
            alignItems="center"
            flexDirection={"column"}
          >
            <Center w={"300px"} h={"150px"} p={4}>
              <IoWarningOutline size={80} />
            </Center>
            <Box textStyle="subtitle" layerStyle="muted">
              {dataNotFoundText}
            </Box>
          </Flex>
        ) : (
          <Flex
            minH="40vh"
            minWidth={{ base: smallWidth ? smallWidth : 800 }}
            justifyContent="center"
            alignItems="center"
          >
            <Box textStyle="subtitle" layerStyle="muted">
              loading
            </Box>
          </Flex>
        )}
      </Box>
      {!hidePagination && (
        <Pagination
          previousPage={previousPage}
          nextPage={nextPage}
          gotoPage={gotoPage}
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          loading={loading}
          page={page}
          totalCount={totalCount}
          pageSize={pageSize}
          setPageSize={setPageSize}
          pageIndex={pageIndex}
          pageOptions={pageOptions}
          getPageIndex={getPageIndex}
          getPageSize={getPageSize}
        />
      )}
    </>
  );
};

const Pagination = ({
  previousPage,
  nextPage,
  gotoPage,
  canPreviousPage,
  canNextPage,
  page,
  totalCount,
  pageSize,
  setPageSize,
  pageIndex,
  pageOptions,
  getPageIndex,
  getPageSize,
}: any) => {
  const intl = useIntl();
  return (
    <Box
      m={2}
      display="flex"
      justifyContent={{ base: "flex-start", md: "space-between" }}
      flexWrap="wrap"
      alignItems="center"
    >
      <Box mt={5} mb={4} display="flex" justifyContent="left">
        {intl.formatMessage(messages.showing)} {pageIndex * pageSize + 1}{" "}
        {intl.formatMessage(messages.to)} {pageIndex * pageSize + page.length}{" "}
        result {intl.formatMessage(messages.of)} {totalCount} results
      </Box>
      <Box
        flexWrap="wrap"
        display="flex"
        justifyContent={{ base: "left", md: "right" }}
        alignItems="center"
      >
        <Flex>
          <Box pt={2} mr={2}>
            {intl.formatMessage(messages.rowPerPage)}:
          </Box>
          <Select
            value={pageSize}
            mr={10}
            width="fit-content"
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              if (getPageSize) getPageSize(Number(e.target.value));
            }}
          >
            {rowsNo.map((item) => (
              <option value={item} key={item}>
                {item}
              </option>
            ))}
          </Select>
        </Flex>
        <Box
          display="flex"
          flexWrap="nowrap"
          alignItems="center"
          my={{ base: 4, md: 0 }}
        >
          <Box mr={5}>
            {intl.formatMessage(messages.page)} {pageIndex + 1}{" "}
            {intl.formatMessage(messages.of)} {pageOptions.length ? pageOptions.length : 1}
          </Box>
          <IconButton
            onClick={() => {
              if (getPageIndex) {
                getPageIndex(pageIndex - 1);
              }
              previousPage();
            }}
            disabled={!canPreviousPage}
            aria-label={""}
          >
            <AiOutlineLeft />
          </IconButton>
          <IconButton
            ml={6}
            onClick={() => {
              if (getPageIndex) {
                getPageIndex(pageIndex + 1);
              }
              nextPage();
            }}
            disabled={!canNextPage}
            aria-label={""}
          >
            <AiOutlineRight />
          </IconButton>
        </Box>
        <Flex>
          <Box pt={2} mr={2} ml={{ base: 0, md: 6 }}>
            {intl.formatMessage(messages.goToPage)}:
          </Box>
          <Box>
            <Input
              onChange={(e) => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                gotoPage(page);
              }}
              defaultValue={pageIndex + 1}
              type="number"
              width={{ base: 20 }}
            />
          </Box>
        </Flex>
      </Box>
    </Box>
  );
};
export default DynamicTable; 